import React, { useState, useEffect } from 'react';
import { Calculator, Ship, RefreshCw, TrendingUp, Clock, AlertCircle, Check } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useExchangeRates } from '../contexts/ExchangeRatesContext';

const TariffCalculator = () => {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Exchange rates from context
  const { 
    rates: exchangeRates, 
    loading: ratesLoading, 
    refreshing: ratesRefreshing,
    error: ratesError,
    refreshRates,
    getUsdRate,
    getEurRate
  } = useExchangeRates();
  
  const [formData, setFormData] = useState({
    gross_tonnage: '',
    net_tonnage: '',
    flag: 'foreign',
    vessel_type: 'cargo',
    anchoring_days: 1
  });

  const [results, setResults] = useState(null);
  const [selectedTariffs, setSelectedTariffs] = useState({});

  useEffect(() => {
    loadSettings();
    refreshRates(false);
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Toggle tariff selection
  const toggleTariff = (index) => {
    setSelectedTariffs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Select all tariffs
  const selectAllTariffs = () => {
    if (results?.tariffs) {
      const allSelected = {};
      results.tariffs.forEach((_, index) => {
        allSelected[index] = true;
      });
      setSelectedTariffs(allSelected);
    }
  };

  // Deselect all tariffs
  const deselectAllTariffs = () => {
    setSelectedTariffs({});
  };

  // Calculate selected totals
  const getSelectedTotals = () => {
    if (!results?.tariffs) return { totalUSD: 0, totalTRY: 0, count: 0 };
    
    let totalUSD = 0;
    let totalTRY = 0;
    let count = 0;
    
    results.tariffs.forEach((tariff, index) => {
      if (selectedTariffs[index]) {
        totalUSD += tariff.amountUSD;
        totalTRY += tariff.amountTRY;
        count++;
      }
    });
    
    return { totalUSD, totalTRY, count };
  };

  const calculateAllTariffs = async () => {
    if (!formData.gross_tonnage || !formData.net_tonnage) {
      toast.error('Lütfen GRT ve NRT değerlerini girin');
      return;
    }

    setCalculating(true);
    setResults(null);
    setSelectedTariffs({});

    const grt = parseFloat(formData.gross_tonnage);
    const nrt = parseFloat(formData.net_tonnage);
    const isTurkish = formData.flag === 'turkish';
    const usdRate = getUsdRate();

    try {
      const tariffs = [];
      let totalUSD = 0;
      const initialSelected = {};

      // 1. Acenta Ücreti (Agency Fee) - USD
      try {
        const response = await api.post('/agency-tariffs/calculate', {
          net_tonnage: nrt,
          stay_days: 1
        });
        if (response.data?.total_fee_usd) {
          const amountUSD = response.data.total_fee_usd;
          tariffs.push({
            name: 'Acenta Ücreti',
            nameEn: 'Agency Fee',
            amountTRY: amountUSD * usdRate,
            amountUSD: amountUSD,
            details: response.data.tier_description || ''
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      } catch (e) { console.log('Agency fee skipped'); }

      // 2. LÇB C Kodu Harcı
      try {
        const response = await api.post(`/port-authority/calculate-lcb-c-code?net_tonnage=${nrt}`);
        if (response.data?.lcb_c_code_try) {
          const amountTRY = response.data.lcb_c_code_try;
          const amountUSD = amountTRY / usdRate;
          tariffs.push({
            name: 'LÇB C Kodu Harcı',
            nameEn: 'Port Authority C-Code Fee',
            amountTRY: amountTRY,
            amountUSD: amountUSD,
            details: response.data.tier_description || ''
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      } catch (e) { console.log('LCB skipped'); }

      // 3. LÇB Fazla Mesai Ücreti
      if (settings) {
        const lcbOvertimeTiers = settings.lcb_overtime_tiers || [];
        let lcbOvertimeFee = 0;
        let tierDesc = '';
        for (const tier of lcbOvertimeTiers) {
          const minNt = tier.min_nt || 0;
          const maxNt = tier.max_nt || Infinity;
          if (nrt >= minNt && nrt <= maxNt) {
            lcbOvertimeFee = tier.fee_try || 0;
            tierDesc = `NT ${minNt}-${maxNt === Infinity ? '∞' : maxNt}`;
            break;
          }
        }
        if (lcbOvertimeFee > 0) {
          const amountUSD = lcbOvertimeFee / usdRate;
          tariffs.push({
            name: 'LÇB Fazla Mesai',
            nameEn: 'Port Authority Overtime',
            amountTRY: lcbOvertimeFee,
            amountUSD: amountUSD,
            details: tierDesc
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      }

      // 4. Ordino Fazla Mesai Ücreti (LÇB / 2)
      if (settings) {
        const lcbOvertimeTiers = settings.lcb_overtime_tiers || [];
        let lcbOvertimeFee = 0;
        for (const tier of lcbOvertimeTiers) {
          const minNt = tier.min_nt || 0;
          const maxNt = tier.max_nt || Infinity;
          if (nrt >= minNt && nrt <= maxNt) {
            lcbOvertimeFee = tier.fee_try || 0;
            break;
          }
        }
        if (lcbOvertimeFee > 0) {
          const ordinoFee = lcbOvertimeFee / 2;
          const amountUSD = ordinoFee / usdRate;
          tariffs.push({
            name: 'Ordino Fazla Mesai',
            nameEn: 'Ordino Overtime',
            amountTRY: ordinoFee,
            amountUSD: amountUSD,
            details: 'LÇB Mesai / 2'
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      }

      // 5. Demirleme Ücreti - TBDTDY Hariç Tarife
      {
        const days = formData.anchoring_days || 1;
        
        // Tarifeler (USD/GT/gün) - TBDTDY Hariç
        let rate;
        let rateDesc;
        
        if (isTurkish) {
          rate = days <= 7 ? 0.002 : 0.003;
          rateDesc = `Türk ${days <= 7 ? '≤7 gün' : '>7 gün'}`;
        } else {
          rate = days <= 7 ? 0.004 : 0.006;
          rateDesc = `Yabancı ${days <= 7 ? '≤7 gün' : '>7 gün'}`;
        }
        
        // Hesaplama: İlk 7 gün + sonrası farklı tarife
        let totalFeeUSD;
        let detailCalc;
        
        if (days <= 7) {
          totalFeeUSD = grt * rate * days;
          detailCalc = `${grt} GT × ${rate} × ${days} gün`;
        } else {
          // İlk 7 gün düşük tarife
          const rateFirst7 = isTurkish ? 0.002 : 0.004;
          // Sonraki günler yüksek tarife
          const rateAfter7 = isTurkish ? 0.003 : 0.006;
          
          const feeFirst7 = grt * rateFirst7 * 7;
          const feeAfter7 = grt * rateAfter7 * (days - 7);
          totalFeeUSD = feeFirst7 + feeAfter7;
          detailCalc = `(7gün×${rateFirst7}) + (${days-7}gün×${rateAfter7})`;
        }
        
        if (totalFeeUSD > 0) {
          tariffs.push({
            name: 'Demirleme Ücreti',
            nameEn: 'Anchoring Fee',
            amountTRY: totalFeeUSD * usdRate,
            amountUSD: totalFeeUSD,
            details: `${detailCalc} - ${rateDesc}`
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += totalFeeUSD;
        }
      }

      // 6. DTO Liman Hizmet Ücreti
      try {
        const isPassenger = formData.vessel_type === 'passenger';
        const response = await api.post(`/dto/calculate?gross_tonnage=${grt}&is_turkish_flag=${isTurkish}&is_passenger=${isPassenger}`);
        if (response.data?.dto_fee_try) {
          const amountTRY = response.data.dto_fee_try;
          const amountUSD = amountTRY / usdRate;
          tariffs.push({
            name: 'DTO Liman Hizmet',
            nameEn: 'DTO Port Service Fee',
            amountTRY: amountTRY,
            amountUSD: amountUSD,
            details: response.data.tier_description || ''
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      } catch (e) { console.log('DTO skipped'); }

      // 7. Fener Ücreti (Light Dues) - Kıyı Emniyeti
      if (settings) {
        let lightDuesFee = 0;
        let detailText = '';
        if (isTurkish) {
          if (nrt <= 800) {
            lightDuesFee = settings.light_dues_turkish_0_800 || 0;
          } else {
            lightDuesFee = settings.light_dues_turkish_800_plus || 0;
          }
          detailText = 'Türk bayrak sabit';
        } else {
          const isPassenger = formData.vessel_type === 'passenger';
          const rate0_800 = isPassenger 
            ? (settings.light_dues_foreign_passenger_0_800 || 0.22176) 
            : (settings.light_dues_foreign_commercial_0_800 || 0.22176);
          const rate800_plus = isPassenger 
            ? (settings.light_dues_foreign_passenger_800_plus || 0.11088) 
            : (settings.light_dues_foreign_commercial_800_plus || 0.11088);
          
          const first800 = Math.min(nrt, 800) * rate0_800;
          const remaining = Math.max(0, nrt - 800) * rate800_plus;
          const singleWay = first800 + remaining;
          lightDuesFee = singleWay * 2; // Giriş + Çıkış
          
          const first800Calc = Math.min(nrt, 800);
          const remainingCalc = Math.max(0, nrt - 800);
          detailText = `(${first800Calc}×${rate0_800} + ${remainingCalc}×${rate800_plus}) × 2`;
        }
        if (lightDuesFee > 0) {
          const amountUSD = isTurkish ? (lightDuesFee / usdRate) : lightDuesFee;
          tariffs.push({
            name: 'Fener Ücreti',
            nameEn: 'Light Dues (Entry + Exit)',
            amountTRY: isTurkish ? lightDuesFee : (lightDuesFee * usdRate),
            amountUSD: amountUSD,
            details: detailText
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      }

      // 9. HSSGM
      if (settings) {
        let hssgmRate = 0;
        let rateDesc = '';
        if (formData.vessel_type === 'passenger') {
          hssgmRate = settings.hssgm_tourist_ship || 0;
          rateDesc = 'Yolcu gemisi';
        } else if (isTurkish) {
          hssgmRate = nrt <= 800 ? (settings.hssgm_cabotage_small || 0) : (settings.hssgm_cabotage_large || 0);
          rateDesc = 'Kabotaj';
        } else {
          hssgmRate = settings.hssgm_commercial || 17.27;
          rateDesc = 'Ticari';
        }
        if (hssgmRate > 0) {
          const amountTRY = nrt * hssgmRate;
          const amountUSD = amountTRY / usdRate;
          tariffs.push({
            name: 'HSSGM',
            nameEn: 'Sanitary Dues',
            amountTRY: amountTRY,
            amountUSD: amountUSD,
            details: `NRT × ${hssgmRate.toFixed(2)} TRY (${rateDesc})`
          });
          initialSelected[tariffs.length - 1] = true;
          totalUSD += amountUSD;
        }
      }

      // 10. Mesai Yolluk
      if (settings?.overtime_fee_try) {
        const amountTRY = settings.overtime_fee_try;
        const amountUSD = amountTRY / usdRate;
        tariffs.push({
          name: 'Mesai Yolluk',
          nameEn: 'Overtime Allowance',
          amountTRY: amountTRY,
          amountUSD: amountUSD,
          details: 'Sabit ücret'
        });
        initialSelected[tariffs.length - 1] = true;
        totalUSD += amountUSD;
      }

      // 11. Transit Vize (Yabancı bayrak)
      if (!isTurkish && settings?.transit_visa_fee_try) {
        const amountTRY = settings.transit_visa_fee_try;
        const amountUSD = amountTRY / usdRate;
        tariffs.push({
          name: 'Transit Vize',
          nameEn: 'Transit Visa Fee',
          amountTRY: amountTRY,
          amountUSD: amountUSD,
          details: 'Yabancı bayrak zorunlu'
        });
        initialSelected[tariffs.length - 1] = true;
        totalUSD += amountUSD;
      }

      setResults({
        tariffs,
        totalUSD,
        totalTRY: totalUSD * usdRate,
        usdRate
      });
      setSelectedTariffs(initialSelected);
      toast.success(`${tariffs.length} tarife hesaplandı`);
    } catch (error) {
      toast.error('Hesaplama sırasında hata oluştu');
      console.error(error);
    } finally {
      setCalculating(false);
    }
  };

  const formatUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatTRY = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatRate = (rate) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    }).format(rate);
  };

  if (loading) {
    return (
      <div className="content">
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
            <RefreshCw className="animate-spin" size={32} />
            <p style={{ marginTop: '1rem' }}>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedTotals = getSelectedTotals();

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">
          <Calculator size={28} style={{ marginRight: '0.5rem' }} />
          Tarife Hesaplama
        </h1>
      </div>

      {/* Exchange Rates Banner */}
      <div style={{ 
        margin: '0 1rem',
        marginTop: '1rem',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        borderRadius: '12px',
        padding: '1rem',
        color: 'white',
        boxShadow: '0 4px 15px rgba(30, 58, 95, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <TrendingUp size={20} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>TCMB Döviz Kurları</div>
              {exchangeRates && (
                <div className="hide-on-mobile" style={{ fontSize: '0.7rem', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={10} />
                  {exchangeRates.date && `${exchangeRates.date}`}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
            {/* USD */}
            <div style={{ 
              background: 'rgba(255,255,255,0.15)', 
              padding: '0.5rem 0.75rem', 
              borderRadius: '8px',
              minWidth: '100px',
              flex: '1 1 100px',
              maxWidth: '140px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.15rem' }}>
                <span style={{ fontSize: '1rem' }}>🇺🇸</span>
                <span style={{ fontWeight: 600, fontSize: '0.75rem' }}>USD/TRY</span>
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                {ratesLoading ? <RefreshCw size={16} className="animate-spin" /> : formatRate(getUsdRate())}
              </div>
            </div>

            {/* EUR */}
            <div style={{ 
              background: 'rgba(255,255,255,0.15)', 
              padding: '0.5rem 0.75rem', 
              borderRadius: '8px',
              minWidth: '100px',
              flex: '1 1 100px',
              maxWidth: '140px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.15rem' }}>
                <span style={{ fontSize: '1rem' }}>🇪🇺</span>
                <span style={{ fontWeight: 600, fontSize: '0.75rem' }}>EUR/TRY</span>
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                {ratesLoading ? <RefreshCw size={16} className="animate-spin" /> : formatRate(getEurRate())}
              </div>
            </div>

            {/* EUR/USD Cross Rate - Hide on very small screens */}
            <div className="hide-on-mobile" style={{ 
              background: 'rgba(255,255,255,0.15)', 
              padding: '0.5rem 0.75rem', 
              borderRadius: '8px',
              minWidth: '100px',
              flex: '1 1 100px',
              maxWidth: '140px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.15rem' }}>
                <span style={{ fontSize: '1rem' }}>💱</span>
                <span style={{ fontWeight: 600, fontSize: '0.75rem' }}>EUR/USD</span>
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                {ratesLoading ? <RefreshCw size={16} className="animate-spin" /> : (getEurRate() / getUsdRate()).toFixed(4)}
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => refreshRates(true)}
              disabled={ratesRefreshing}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                color: 'white',
                cursor: ratesRefreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontWeight: 600,
                fontSize: '0.75rem',
                transition: 'all 0.2s'
              }}
            >
              <RefreshCw size={14} className={ratesRefreshing ? 'animate-spin' : ''} />
              <span className="hide-on-mobile">{ratesRefreshing ? 'Güncelleniyor...' : 'Güncelle'}</span>
            </button>
          </div>
        </div>

        {ratesError && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.75rem 1rem', 
            background: 'rgba(239, 68, 68, 0.2)', 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={18} />
            <span>{ratesError} - Varsayılan kurlar kullanılıyor</span>
          </div>
        )}
      </div>

      <div style={{ padding: '1rem' }} className="tariff-calculator-content">
        <div style={{ display: 'grid', gap: '1.5rem' }} className="tariff-calculator-grid">
          {/* Input Form */}
          <div className="card">
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)', color: 'white' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Ship size={20} />
                Gemi Bilgileri
              </h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Gross Tonaj (GRT) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.gross_tonnage}
                  onChange={(e) => setFormData({ ...formData, gross_tonnage: e.target.value })}
                  placeholder="Örn: 5000"
                  data-testid="grt-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Net Tonaj (NRT) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.net_tonnage}
                  onChange={(e) => setFormData({ ...formData, net_tonnage: e.target.value })}
                  placeholder="Örn: 2500"
                  data-testid="nrt-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Bayrak</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    border: formData.flag === 'turkish' ? '2px solid #dc2626' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.flag === 'turkish' ? '#fef2f2' : 'white',
                    flex: 1
                  }}>
                    <input
                      type="radio"
                      name="flag"
                      value="turkish"
                      checked={formData.flag === 'turkish'}
                      onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                    />
                    <span style={{ fontSize: '1.25rem' }}>🇹🇷</span>
                    <span style={{ fontWeight: formData.flag === 'turkish' ? 600 : 400, fontSize: '0.875rem' }}>Türk</span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    border: formData.flag === 'foreign' ? '2px solid #2563eb' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.flag === 'foreign' ? '#eff6ff' : 'white',
                    flex: 1
                  }}>
                    <input
                      type="radio"
                      name="flag"
                      value="foreign"
                      checked={formData.flag === 'foreign'}
                      onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                    />
                    <span style={{ fontSize: '1.25rem' }}>🌍</span>
                    <span style={{ fontWeight: formData.flag === 'foreign' ? 600 : 400, fontSize: '0.875rem' }}>Yabancı</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Gemi Tipi</label>
                <select
                  className="form-input"
                  value={formData.vessel_type}
                  onChange={(e) => setFormData({ ...formData, vessel_type: e.target.value })}
                >
                  <option value="cargo">Kargo / Dökme Yük</option>
                  <option value="tanker">Tanker</option>
                  <option value="passenger">Yolcu / Kruvaziyer</option>
                  <option value="container">Konteyner</option>
                  <option value="other">Diğer</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Demirleme Süresi (Gün)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.anchoring_days}
                  onChange={(e) => setFormData({ ...formData, anchoring_days: parseInt(e.target.value) || 1 })}
                  min="1"
                  placeholder="1"
                />
              </div>

              <button
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '1rem', padding: '0.875rem' }}
                onClick={calculateAllTariffs}
                disabled={calculating}
                data-testid="calculate-btn"
              >
                {calculating ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" style={{ marginRight: '0.5rem' }} />
                    Hesaplanıyor...
                  </>
                ) : (
                  <>
                    <Calculator size={18} style={{ marginRight: '0.5rem' }} />
                    Tüm Tarifeleri Hesapla
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="card">
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Hesaplama Sonuçları</h3>
                {results?.tariffs && results.tariffs.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={selectAllTariffs}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Tümünü Seç
                    </button>
                    <button
                      onClick={deselectAllTariffs}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Temizle
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {!results ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
                  <Calculator size={64} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                  <p style={{ fontSize: '1.1rem' }}>GRT ve NRT değerlerini girip</p>
                  <p style={{ fontSize: '1.1rem' }}>"Tüm Tarifeleri Hesapla" butonuna tıklayın</p>
                </div>
              ) : (
                <>
                  {/* Summary Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem 1.5rem',
                    background: '#f0fdf4',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Gemi Bilgileri</div>
                      <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        GRT: {formData.gross_tonnage} | NRT: {formData.net_tonnage}
                      </div>
                      <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {formData.flag === 'turkish' ? '🇹🇷 Türk Bayraklı' : '🌍 Yabancı Bayraklı'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Hesaplamada Kullanılan Kur</div>
                      <div style={{ fontWeight: 600, color: '#059669' }}>1 USD = {formatRate(results.usdRate)} TRY</div>
                    </div>
                  </div>

                  {/* Tariff List with Checkboxes */}
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                        <tr>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', width: '50px' }}>✓</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b' }}>Tarife</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b' }}>Detay</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b' }}>TRY</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b' }}>USD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.tariffs.map((tariff, index) => (
                          <tr 
                            key={index} 
                            style={{ 
                              borderBottom: '1px solid #f1f5f9',
                              background: selectedTariffs[index] ? '#f0fdf4' : 'white',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                            onClick={() => toggleTariff(index)}
                          >
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: selectedTariffs[index] ? '2px solid #059669' : '2px solid #cbd5e1',
                                background: selectedTariffs[index] ? '#059669' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                transition: 'all 0.2s'
                              }}>
                                {selectedTariffs[index] && <Check size={14} color="white" />}
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ fontWeight: 600, color: selectedTariffs[index] ? '#059669' : '#1e293b' }}>{tariff.name}</div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{tariff.nameEn}</div>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.8rem' }}>
                              {tariff.details}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#64748b', fontSize: '0.875rem' }}>
                              {formatTRY(tariff.amountTRY)}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                              <span style={{ 
                                fontWeight: 600,
                                color: selectedTariffs[index] ? '#059669' : '#2563eb',
                                fontSize: '0.9rem'
                              }}>
                                {formatUSD(tariff.amountUSD)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Selected Total */}
                  <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #1e40af, #2563eb)',
                    color: 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                          SEÇİLİ TOPLAM ({selectedTotals.count} / {results.tariffs.length} kalem)
                        </div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                          TL Karşılığı: {formatTRY(selectedTotals.totalTRY)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '2.5rem', 
                          fontWeight: 700,
                          textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {formatUSD(selectedTotals.totalUSD)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TariffCalculator;
